import type { LLMSettings, MCPConfig } from "@/types";

const STORAGE_KEY = "chatmcp:llm-settings";
const MCP_CONFIG_KEY = "chatmcp:mcp-config";

const ollamaSettings: Partial<LLMSettings> = {
  provider: "ollama",
  model: "llama3.1",
  baseUrl: "http://localhost:11434/api",
  allowExternal: false,
  temperature: 0.2,
};

const openAICompatibleSettings: Partial<LLMSettings> = {
  provider: "openai-compatible",
  model: "gpt-3.5-turbo",
  baseUrl: "https://api.openai.com/v1",
  allowExternal: true,
  temperature: 0.7,
};

const cloudflareWorkersAISettings: LLMSettings = {
  provider: "cloudflare-workers-ai",
  model: "@cf/meta/llama-3.1-8b-instruct",
  gatewayId: "chatmcp",
  allowExternal: true,
  temperature: 0.7,
};

export const defaultLLMSettings: LLMSettings = {
  ...cloudflareWorkersAISettings
};

export const loadLLMSettings = (): LLMSettings => {
  if (typeof localStorage === "undefined") return defaultLLMSettings;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultLLMSettings;
    const parsed = JSON.parse(raw) as Partial<LLMSettings>;
    return {
      ...defaultLLMSettings,
      ...parsed,
    };
  } catch {
    return defaultLLMSettings;
  }
};

export const saveLLMSettings = (settings: LLMSettings) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Best-effort cache; ignore storage failures.
  }
};

export const isLocalLLM = (settings: LLMSettings): boolean => {
  // Cloudflare Workers AI is always external
  if (settings.provider === "cloudflare-workers-ai") return false;
  
  if (!settings.baseUrl) return true;
  try {
    const url = new URL(settings.baseUrl);
    return ["localhost", "127.0.0.1", "[::1]", "::1"].includes(url.hostname);
  } catch {
    return false;
  }
};

export const defaultMCPConfig: MCPConfig = {
  keepAlive: true,
  baseDelay: 120,
  maxDelay: 600,
  maxAttempts: 4,
  connectionTimeout: 30,
  requestTimeout: 60,
  enableLogging: true,
  autoReconnect: true,
  enableMetrics: false,
  enableCaching: false,
  compressionEnabled: false,
  rateLimitPerMinute: 60,
  maxConcurrentRequests: 5,
};

export const loadMCPConfig = (): MCPConfig => {
  if (typeof localStorage === "undefined") return defaultMCPConfig;
  try {
    const raw = localStorage.getItem(MCP_CONFIG_KEY);
    if (!raw) return defaultMCPConfig;
    const parsed = JSON.parse(raw) as Partial<MCPConfig>;
    return {
      ...defaultMCPConfig,
      ...parsed,
    };
  } catch {
    return defaultMCPConfig;
  }
};

export const saveMCPConfig = (config: MCPConfig) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(MCP_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // Best-effort cache; ignore storage failures.
  }
};
